import { v4 as uuidV4 } from 'uuid'

const SAML_FLOW_T0_KEY = 'saml_flow_t0'
const SAML_FLOW_CORRELATION_ID_KEY = 'saml_flow_correlation_id'

// Persisted in sessionStorage (not performance.now()) because the SAML webview flow
// spans full-page redirects to Okta's hosted UI and back, which reset performance.now().
//
// Returns true only the first time it runs for this flow (t0 not yet set) — the effect
// that calls this re-fires on every asPath change while isWebview stays truthy in
// sessionStorage, so callers must use this to log the render_start hop exactly once.
export function startSamlFlowTimer(correlationId?: string): boolean {
  try {
    const alreadyStarted = !!sessionStorage.getItem(SAML_FLOW_T0_KEY)
    if (!alreadyStarted) {
      sessionStorage.setItem(SAML_FLOW_T0_KEY, String(Date.now()))
    }
    // Falls back to a web-generated UUID when the native app doesn't pass one yet,
    // so every hop log always carries a correlationId instead of sometimes omitting it.
    if (!sessionStorage.getItem(SAML_FLOW_CORRELATION_ID_KEY)) {
      sessionStorage.setItem(SAML_FLOW_CORRELATION_ID_KEY, correlationId || uuidV4())
    }
    return !alreadyStarted
  } catch {
    /* sessionStorage unavailable (private mode, SSR) */
    return false
  }
}

export function getSamlFlowElapsedMs(): number | undefined {
  try {
    const t0 = sessionStorage.getItem(SAML_FLOW_T0_KEY)
    return t0 ? Date.now() - Number(t0) : undefined
  } catch {
    return undefined
  }
}

// Set once startSamlFlowTimer has run: the native app's correlationId when passed on the
// webview URL, otherwise a web-generated UUID fallback. undefined only if sessionStorage
// is unavailable or the timer was never started (i.e. not a SAML webview flow).
export function getSamlFlowCorrelationId(): string | undefined {
  try {
    return sessionStorage.getItem(SAML_FLOW_CORRELATION_ID_KEY) ?? undefined
  } catch {
    return undefined
  }
}

export function clearSamlFlowTimer(): void {
  try {
    sessionStorage.removeItem(SAML_FLOW_T0_KEY)
    sessionStorage.removeItem(SAML_FLOW_CORRELATION_ID_KEY)
  } catch {
    /* sessionStorage unavailable (private mode, SSR) */
  }
}
